import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  CopyIcon,
  CheckIcon,
  XIcon,
  ShareNetworkIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useCreateShareableLink,
  getShareableLinkUrl,
} from 'src/api/core/useShareableLinks';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import { T, Var, useGT } from 'gt-react';

type ReportLineType = 'estimates' | 'predictions' | 'reported';

export interface SharedFilterDef {
  key: string;
  label: string;
  options: { id: string; label: string }[];
  /** How the receiver sees this filter: 'single' picks one at a time, 'multi' allows combining. Defaults to 'multi'. */
  displayType?: 'single' | 'multi';
}

export interface CustomFilterConfig {
  key: string;
  label: string;
  options: { id: string; label: string }[];
  initialValue?: string[];
  placeholder?: string;
  /** How the receiver sees this filter. Defaults to 'multi'. */
  receiverDisplayType?: 'single' | 'multi';
}

interface ShareReportModalProps {
  open: boolean;
  onClose: () => void;
  reportId: string;
  reportTitle: string;
  /** Initial year filter value (defaults to current year) */
  initialYear?: number;
  /** Initial division filter values (defaults to empty) */
  initialDivisions?: MultiSelectOption[];
  /** Initial line type filter value (defaults to 'reported') */
  initialLineType?: ReportLineType;
  /**
   * Custom filters to display in the modal.
   * These are rendered as additional dropdowns and their values are saved in metadata.
   */
  customFilters?: CustomFilterConfig[];
  /**
   * Static metadata to include in the shareable link without UI.
   * Use this for values that don't need to be changed by the user (e.g., viewMode, month).
   * For interactive filters, use customFilters instead.
   */
  additionalMetadata?: Record<string, unknown>;
  /**
   * Hide all filter options (year, divisions, line type, custom filters).
   * Use this for reports that should share all data without filter restrictions.
   */
  hideFilters?: boolean;
}

export function ShareReportModal({
  open,
  onClose,
  reportId,
  reportTitle,
  initialYear,
  initialDivisions,
  initialLineType,
  hideFilters = false,
  customFilters = [],
  additionalMetadata,
}: ShareReportModalProps) {
  const gt = useGT();
  const currentYear = new Date().getFullYear();

  const lineTypeOptions = useMemo(
    () =>
      [
        {
          id: 'reported' as const,
          label: gt('Reported', { context: 'rebates report line type filter' }),
          value: 'reported' as const,
        },
        {
          id: 'estimates' as const,
          label: gt('Estimates', {
            context: 'rebates report line type filter',
          }),
          value: 'estimates' as const,
        },
        {
          id: 'predictions' as const,
          label: gt('Predictions', {
            context: 'rebates report line type filter',
          }),
          value: 'predictions' as const,
        },
      ] satisfies {
        id: ReportLineType;
        label: string;
        value: ReportLineType;
      }[],
    [gt],
  );
  const defaultYear = initialYear ?? currentYear;
  const defaultDivisions = useMemo(
    () => initialDivisions ?? [],
    [initialDivisions],
  );
  const defaultLineType = initialLineType ?? 'reported';

  const [linkName, setLinkName] = useState('');

  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [selectedYear, setSelectedYear] = useState<number>(defaultYear);
  const [selectedDivisions, setSelectedDivisions] =
    useState<MultiSelectOption[]>(defaultDivisions);
  const [selectedLineTypes, setSelectedLineTypes] = useState<
    MultiSelectOption[]
  >(lineTypeOptions.filter((opt) => opt.value === defaultLineType));

  // Custom filter state - all values are arrays
  const [customFilterValues, setCustomFilterValues] = useState<
    Record<string, string[]>
  >({});

  // Track if we've initialized for current open state
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (open && !hasInitializedRef.current) {
      const initial: Record<string, string[]> = {};
      customFilters.forEach((filter) => {
        if (filter.initialValue !== undefined) {
          initial[filter.key] = filter.initialValue;
        }
      });
      setCustomFilterValues(initial);
      hasInitializedRef.current = true;
    } else if (!open) {
      hasInitializedRef.current = false;
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [open]);

  const createShareableLink = useCreateShareableLink();

  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  const { data: availableYearsData } = useRebateAgreementYears();

  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  const yearOptions = useMemo(() => {
    const years =
      availableYearsData && 'years' in availableYearsData
        ? availableYearsData.years
        : [];
    // Ensure current year is always available
    const allYears = [...new Set([currentYear, ...years])].sort(
      (a, b) => b - a,
    );
    return allYears.map((year) => ({
      id: year.toString(),
      label: year.toString(),
      value: year,
    }));
  }, [availableYearsData, currentYear]);

  const showYearFilter = true; // All reports use year
  const showDivisionFilter = true; // All reports support division filtering
  const showLineTypeFilter =
    reportId === 'division-rebate-summary' ||
    reportId === 'supplier-tag-summary';

  function handleAddEmail() {
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (
      trimmedEmail &&
      isValidEmail(trimmedEmail) &&
      !emails.includes(trimmedEmail)
    ) {
      setEmails([...emails, trimmedEmail]);
      setEmailInput('');
    }
  }

  function handleRemoveEmail(emailToRemove: string) {
    setEmails(emails.filter((email) => email !== emailToRemove));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  }

  function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async function handleCreateLink() {
    const metadata: Record<string, unknown> = {
      ...additionalMetadata,
    };

    if (!hideFilters) {
      metadata.year = selectedYear;

      if (selectedDivisions.length > 0) {
        metadata.divisionIds = selectedDivisions.map((d) => d.id);
      }

      if (showLineTypeFilter && selectedLineTypes.length > 0) {
        metadata.lineTypes = selectedLineTypes.map((lt) => lt.id);
      }

      Object.entries(customFilterValues).forEach(([key, value]) => {
        if (value && value.length > 0) {
          metadata[key] = value;
        }
      });

      // Build sharedFilters for receiver-side filter bar
      const sharedFilters: SharedFilterDef[] = [];

      if (selectedDivisions.length > 0) {
        sharedFilters.push({
          key: 'divisionIds',
          label: gt('Divisions', { context: 'shared filter label' }),
          options: selectedDivisions.map((d) => ({
            id: d.id,
            label: d.label,
          })),
        });
      }

      if (showLineTypeFilter && selectedLineTypes.length > 0) {
        sharedFilters.push({
          key: 'lineTypes',
          label: gt('Line Type', { context: 'shared filter label' }),
          options: selectedLineTypes.map((lt) => ({
            id: lt.id,
            label: lt.label,
          })),
          displayType: 'single',
        });
      }

      customFilters.forEach((filter) => {
        const values = customFilterValues[filter.key] ?? [];
        if (values.length > 0) {
          sharedFilters.push({
            key: filter.key,
            label: filter.label,
            options: filter.options.filter((opt) => values.includes(opt.id)),
            displayType: filter.receiverDisplayType,
          });
        }
      });

      if (sharedFilters.length > 0) {
        metadata.sharedFilters = sharedFilters;
      }
    }

    const result = await createShareableLink.mutateAsync({
      name: linkName.trim() || null,
      resourceType: `report:${reportId}`,
      metadata,
      allowedEmails: emails,
    });

    if (result.data?.shareableLink) {
      const url = getShareableLinkUrl(result.data.shareableLink.token);
      setGeneratedLink(url);
    }
  }

  function handleCopyLink() {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  // Sync filter state when modal opens with new initial values
  React.useEffect(() => {
    if (open) {
      setSelectedYear(defaultYear);
      setSelectedDivisions(defaultDivisions);
      setSelectedLineTypes(
        lineTypeOptions.filter((opt) => opt.value === defaultLineType),
      );
    }
  }, [open, defaultYear, defaultDivisions, defaultLineType, lineTypeOptions]);

  function handleClose() {
    setLinkName('');
    setEmails([]);
    setEmailInput('');
    setGeneratedLink(null);
    setCopied(false);
    setSelectedYear(defaultYear);
    setSelectedDivisions(defaultDivisions);
    setSelectedLineTypes(
      lineTypeOptions.filter((opt) => opt.value === defaultLineType),
    );
    const initial: Record<string, string[]> = {};
    customFilters.forEach((filter) => {
      if (filter.initialValue !== undefined) {
        initial[filter.key] = filter.initialValue;
      }
    });
    setCustomFilterValues(initial);
    onClose();
  }

  const canCreateLink = emails.length > 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShareNetworkIcon className="h-5 w-5" />
            <T context="share report modal title">Share Report</T>
          </DialogTitle>
          <DialogDescription>
            <T context="share report modal description">
              Share &quot;<Var>{reportTitle}</Var>&quot; with specific people.
              They will need to log in to view this report.
            </T>
          </DialogDescription>
        </DialogHeader>

        {!generatedLink ? (
          <div className="space-y-4">
            {/* Link Name */}
            <div className="space-y-2">
              <Label htmlFor="linkName">
                {gt('Link Name (optional)', {
                  context: 'share report link name field',
                })}
              </Label>
              <Input
                id="linkName"
                placeholder={gt('e.g., Q4 2024 Rebate Summary for Acme Corp', {
                  context: 'share report link name placeholder',
                })}
                value={linkName}
                onChange={(e) => setLinkName(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                {gt(
                  'Give this link a descriptive name. Recipients will see this as the report title.',
                  { context: 'share report link name help' },
                )}
              </p>
            </div>

            {/* Email Section */}
            <div className="space-y-2">
              <Label htmlFor="email">
                {gt('Share with (email addresses)', {
                  context: 'share report email field',
                })}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="email"
                  type="email"
                  placeholder={gt('Enter email address', {
                    context: 'share report email placeholder',
                  })}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddEmail}
                  disabled={
                    !emailInput.trim() || !isValidEmail(emailInput.trim())
                  }
                >
                  {gt('Add', { context: 'add email to share list' })}
                </Button>
              </div>
              {emails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {emails.map((email) => (
                    <Badge
                      key={email}
                      variant="secondary"
                      className="flex items-center gap-1 pr-1"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        className="ml-1 rounded-full hover:bg-muted p-0.5"
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              {emails.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {gt('Add at least one email address to share this report.', {
                    context: 'share report validation hint',
                  })}
                </p>
              )}
            </div>

            {/* Filters Section - hidden when hideFilters is true */}
            {!hideFilters && (
              <div className="border-t pt-4">
                <Label className="text-sm font-medium mb-3 block">
                  {gt('Report Filters', {
                    context: 'share report filters section',
                  })}
                </Label>
                <p className="text-sm text-muted-foreground mb-3">
                  {gt(
                    'Set the default filters for this shared report. Recipients will see the report with these filters applied.',
                    { context: 'share report filters help' },
                  )}
                </p>
                <div className="space-y-3">
                  {showYearFilter && (
                    <SingleSelect
                      label={gt('Year', {
                        context: 'share report year filter label',
                      })}
                      options={yearOptions}
                      value={
                        yearOptions.find((opt) => opt.value === selectedYear) ||
                        null
                      }
                      onChange={(option) => {
                        if (option) {
                          setSelectedYear(option.value);
                        }
                      }}
                      placeholder={gt('Select year...', {
                        context: 'share report year filter',
                      })}
                      searchPlaceholder={gt('Search years...', {
                        context: 'share report year filter',
                      })}
                    />
                  )}

                  {showDivisionFilter && (
                    <MultiSelect
                      label={gt('Divisions', {
                        context: 'share report division filter label',
                      })}
                      options={divisionOptions}
                      value={selectedDivisions}
                      onChange={setSelectedDivisions}
                      placeholder={gt('All divisions (no filter)', {
                        context: 'share report division filter',
                      })}
                      searchPlaceholder={gt('Search divisions...', {
                        context: 'share report division filter',
                      })}
                      disabled={divisionsLoading}
                    />
                  )}

                  {showLineTypeFilter && (
                    <MultiSelect
                      label={gt('Line Type', {
                        context: 'share report line type filter label',
                      })}
                      options={lineTypeOptions}
                      value={selectedLineTypes}
                      onChange={setSelectedLineTypes}
                      placeholder={gt('Select line types...', {
                        context: 'share report line type filter',
                      })}
                    />
                  )}

                  {customFilters.map((filter) => {
                    const options = filter.options.map((opt) => ({
                      id: opt.id,
                      label: opt.label,
                      value: opt.id,
                    }));
                    const currentValue = customFilterValues[filter.key] ?? [];
                    const selectedOptions = options.filter((opt) =>
                      currentValue.includes(opt.id),
                    );
                    return (
                      <MultiSelect
                        key={filter.key}
                        label={filter.label}
                        options={options}
                        value={selectedOptions}
                        onChange={(selected) => {
                          setCustomFilterValues((prev) => ({
                            ...prev,
                            [filter.key]: selected.map((s) => s.id),
                          }));
                        }}
                        placeholder={
                          filter.placeholder ??
                          gt('Select {label}...', {
                            label: filter.label.toLowerCase(),
                            context: 'share report custom filter placeholder',
                          })
                        }
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>
                {gt('Shareable link', {
                  context: 'generated shareable link field label',
                })}
              </Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={generatedLink}
                  className="font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                >
                  {copied ? (
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  ) : (
                    <CopyIcon className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="text-sm text-muted-foreground space-y-1">
              {linkName.trim() && (
                <p>
                  <T context="share link summary">
                    <strong>
                      <T context="label">Name:</T>
                    </strong>{' '}
                    <Var>{linkName.trim()}</Var>
                  </T>
                </p>
              )}
              <p>
                <T context="share link summary">
                  <strong>
                    <T context="label">Shared with:</T>
                  </strong>{' '}
                  <Var>{emails.join(', ')}</Var>
                </T>
              </p>
              {!hideFilters && (
                <>
                  <p>
                    <T context="share link summary">
                      <strong>
                        <T context="label">Year:</T>
                      </strong>{' '}
                      <Var>{selectedYear}</Var>
                    </T>
                  </p>
                  {selectedDivisions.length > 0 && (
                    <p>
                      <T context="share link summary">
                        <strong>
                          <T context="label">Divisions:</T>
                        </strong>{' '}
                        <Var>
                          {selectedDivisions.map((d) => d.label).join(', ')}
                        </Var>
                      </T>
                    </p>
                  )}
                </>
              )}
              {!hideFilters &&
                showLineTypeFilter &&
                selectedLineTypes.length > 0 && (
                  <p>
                    <T context="share link summary">
                      <strong>
                        <T context="label">Line Type:</T>
                      </strong>{' '}
                      <Var>
                        {selectedLineTypes.map((lt) => lt.label).join(', ')}
                      </Var>
                    </T>
                  </p>
                )}
              {customFilters.map((filter) => {
                const value = customFilterValues[filter.key];
                if (!value || value.length === 0) {
                  return null;
                }

                const getLabel = (id: string) =>
                  filter.options.find((opt) => opt.id === id)?.label ?? id;

                return (
                  <p key={filter.key}>
                    <T context="share link custom filter summary">
                      <strong>
                        <Var>{filter.label}</Var>:
                      </strong>{' '}
                      <Var>{value.map(getLabel).join(', ')}</Var>
                    </T>
                  </p>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {generatedLink ? (
              <T context="dialog button">Done</T>
            ) : (
              <T context="dialog button">Cancel</T>
            )}
          </Button>
          {!generatedLink && (
            <Tooltip open={!canCreateLink ? undefined : false}>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button
                    onClick={handleCreateLink}
                    disabled={createShareableLink.isPending || !canCreateLink}
                  >
                    {createShareableLink.isPending && (
                      <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    <T context="create shareable link button">Create Link</T>
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {gt('Add at least one email address to share this report', {
                  context: 'create share link disabled tooltip',
                })}
              </TooltipContent>
            </Tooltip>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
